<template>
  <section class="interview-section">
    <h2>{{ $t('interview.title') }}</h2>
    <div class="interview-stack">
    <div
      v-for="(card, index) in visibleCards"
      :key="index"
      :class="['card', { top: index === 0, middle: index === 1, bottom: index === 2 }]"
      :style="{ backgroundColor: card.color, color: card.fontColor }"
      :role="index === 0 ? 'button' : undefined"
      :tabindex="index === 0 ? 0 : -1"
      :aria-label="index === 0 ? card.title : undefined"
      @mouseover="tiltCard(index)"
      @mouseleave="resetTilt"
      @click="cycleCards"
      @keydown.enter.prevent="cycleCards"
      @keydown.space.prevent="cycleCards"
    >
      <div v-if="card.key === 'cover'" class="cover-content">
        <h3>{{ card.title }}</h3>
        <p class="subtitle">{{ card.subtitle }}</p>
        <p>{{ card.interview }}</p>
        <img :src="card.image" alt="Cover Image" class="cover-image" />
        <img :src="card.image2" alt="Cover Image" class="cover-image2" />
        <div class="page-number">{{ card.page }}</div>
      </div>
      <div v-else>
        <h3 class="question-title" v-html="card.title"></h3>
        <p class="answer" v-html="card.answer"></p>
        <div class="page-number">{{ card.page }}</div>
      </div>
      <div class="arrow">➔</div>
    </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import coverImage from '@/assets/interview/zeno.png';
import coverImage2 from '@/assets/interview/fondation-bernheim.png';
const { t } = useI18n();

const rawCards = [
  {
    key: 'cover',
    color: '#26394d',
    fontColor: '#ffffff',
    page: 1,
    image: coverImage,
    image2: coverImage2
  },
  {
    key: 'q1',
    color: '#f8f2eb',
    fontColor: '#26394d',
    page: 2
  },
  {
    key: 'q2',
    color: '#c93b45',
    fontColor: '#ffffff',
    page: 3
  },
  {
    key: 'q3',
    color: '#d8a932',
    fontColor: '#26394d',
    page: 4
  },
  {
    key: 'q4',
    color: '#ffffff',
    fontColor: '#002e41',
    page: 5
  }
];

const cards = ref([]);

watchEffect(() => {
  cards.value = rawCards.map(card => ({
    ...card,
    title: card.key === 'cover' ? t('interview.content.cover.title') : t(`interview.content.${card.key}.title`),
    subtitle: card.key === 'cover' ? t('interview.content.cover.subtitle') : '',
    interview: card.key === 'cover' ? t('interview.content.cover.interview') : '',
    answer: card.key !== 'cover' ? t(`interview.content.${card.key}.answer`) : ''
  }));
});

const visibleCards = computed(() => cards.value.slice(0, 3));

const cycleCards = () => {
  const firstCard = cards.value.shift();
  cards.value.push(firstCard);
};

const tiltCard = (index) => {
  if (index === 0) {
    document.querySelector('.card.top').style.transformOrigin = 'top left';
    document.querySelector('.card.top').style.transform = 'rotateX(-5deg) rotateY(-5deg)';
  }
};

const resetTilt = () => {
  document.querySelector('.card.top').style.transform = 'rotateX(0) rotateY(0)';
};
</script>

<style scoped>
.interview-section {
  padding: clamp(80px, 9vw, 120px) 20px clamp(110px, 11vw, 150px);
  overflow: hidden;
  background: var(--secondary-color);
}

.interview-stack {
  position: relative;
  width: calc(100% - 72px);
  max-width: 820px;
  height: 470px;
  margin: 0 auto;
  perspective: 1200px;
}

.interview-section h2 {
  margin-bottom: 4rem;
  color: #fff;
}

.interview-section h2::after {
  background: linear-gradient(90deg, var(--primary-color) 0 72%, var(--accent-color) 72% 100%);
}

.card {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 28px;
  box-shadow: 0 30px 70px rgba(11, 22, 34, 0.3);
  transition: transform 0.3s ease, background-color 0.3s ease;
  cursor: pointer;
  padding: clamp(30px, 5vw, 54px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  overflow: hidden;
}

.card h3 {
  color: inherit;
}

.cover-content {
  display: flex;
  flex-direction: column;
  width: 58%;
  height: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  box-sizing: border-box;
  padding-top: clamp(28px, 4vw, 36px);
  text-align: left;
}

.cover-content h3 {
  max-width: 430px;
  margin: 0 0 14px;
  font-size: clamp(2rem, 4vw, 3.1rem);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.045em;
}

.cover-content .subtitle {
  margin: 0 0 14px;
  color: #f0ce71;
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  font-weight: 650;
  line-height: 1.35;
}

.cover-content p {
  max-width: 420px;
  margin: 0;
  font-size: 1.02rem;
}

.cover-content > p:not(.subtitle) {
  display: none;
}

.cover-image {
  width: 42%;
  max-width: 340px;
  height: 92%;
  margin: 0;
  position: absolute;
  right: 20px;
  bottom: 0;
  object-fit: contain;
  object-position: bottom;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.cover-image2 {
  width: auto;
  max-width: 40%;
  margin-bottom: 1rem;
  padding: 0;
  position: absolute;
  bottom: -1rem;
  left: 0;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.page-number {
  position: absolute;
  right: 22px;
  bottom: 20px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.95rem;
  font-weight: bold;
  color: black;
}

.top {
  z-index: 3;
}

.middle {
  z-index: 2;
  transform: rotate(2.2deg) translate(26px, 22px);
  opacity: 0.82;
}

.bottom {
  z-index: 1;
  transform: rotate(-2.2deg) translate(-26px, 36px);
  opacity: 0.62;
}

.middle > *,
.bottom > * {
  visibility: hidden;
}

.question-title {
  max-width: 680px;
  margin-bottom: 24px;
  font-size: clamp(1.55rem, 3vw, 2.25rem);
  font-weight: 800;
  line-height: 1.16;
  letter-spacing: -0.025em;
}

.answer {
  max-width: 720px;
  margin-bottom: 20px;
  font-size: clamp(0.98rem, 1.35vw, 1.08rem);
  line-height: 1.72;
  text-align: left;
}

.arrow {
  position: absolute;
  top: 22px;
  right: 22px;
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  font-size: 1.35rem;
}

/* Mobile styles */
@media (max-width: 768px) {
  .interview-section {
    padding: 70px 14px 84px;
  }

  .interview-section h2 {
    margin-bottom: 3rem;
  }

  .interview-stack {
    width: 100%;
    height: 520px;
  }

  .card {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 28px;
    position: absolute;
    transform: none !important;
    border-radius: 22px;
  }

  .cover-content {
    width: 72%;
    justify-content: flex-start;
    padding-top: 0;
  }

  .cover-content h3 {
    max-width: 260px;
    font-size: 2.1rem;
  }

  .cover-content .subtitle {
    font-size: 1.2rem;
  }

  .cover-content p {
    max-width: 250px;
    font-size: 0.94rem;
  }

  .cover-image {
    width: 55%;
    height: 58%;
    right: -18px;
    bottom: 0;
    z-index: 1;
  }

  .cover-image2 {
    z-index: 2;
    width: auto;
    max-width: 60%;
    bottom: -1rem;
    left: 0;
  }

  .page-number {
    width: 25px;
    height: 25px;
    font-size: 1rem;
    z-index: 3;
  }

  .top {
    z-index: 3;
  }

  .middle,
  .bottom {
    display: none;
  }

  .arrow {
    top: 16px;
    right: 16px;
  }

  .answer {
    max-height: 360px;
    overflow-y: auto;
  }
}
</style>
